import { useCallback, useState } from 'react';
import { useToast } from '@/contexts/admin/ToastContext';
import {
  usePutBankAccountMutation,
  usePutCreditCardMutation,
  useDeleteBankAccountMutation,
  useDeleteCreditCardMutation,
} from '@/store/api';
import { isPaymentMethodBank, getPaymentMethodSourceId } from '@/utils/residents/paymentUtils';
import { extractErrorMessage } from '@/utils/common/errorHandling';

/**
 * Shared hook for payment method management actions
 * Handles set as default and delete operations for payment methods
 */
export const usePaymentMethodActions = (onRefresh?: () => void) => {
  const { showSuccess, showError } = useToast();

  const [putBankAccount, { isLoading: isUpdatingBank }] = usePutBankAccountMutation();
  const [putCreditCard, { isLoading: isUpdatingCard }] = usePutCreditCardMutation();
  const [deleteBankAccount, { isLoading: isDeletingBank }] = useDeleteBankAccountMutation();
  const [deleteCreditCard, { isLoading: isDeletingCard }] = useDeleteCreditCardMutation();

  const isUpdating = isUpdatingBank || isUpdatingCard;
  const isDeleting = isDeletingBank || isDeletingCard;

  const [methodToDelete, setMethodToDelete] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /**
   * Set a payment method as default
   */
  const setAsDefault = useCallback(
    async (method: any) => {
      try {
        const sourceId = getPaymentMethodSourceId(method);

        if (isPaymentMethodBank(method)) {
          await putBankAccount({ sourceId, bankAccount: { isDefault: true } }).unwrap();
        } else {
          await putCreditCard({ sourceId, creditCard: { isDefault: true } }).unwrap();
        }

        onRefresh?.();
        showSuccess('Default payment method updated');
      } catch (error) {
        console.error('Set as default error:', error);
        showError(extractErrorMessage(error, 'Failed to set default payment method'));
        throw error;
      }
    },
    [putBankAccount, putCreditCard, onRefresh, showSuccess, showError]
  );

  /**
   * Delete a payment method
   */
  const deleteMethod = useCallback(
    async (method: any) => {
      try {
        const sourceId = getPaymentMethodSourceId(method);

        if (isPaymentMethodBank(method)) {
          await deleteBankAccount({ sourceId }).unwrap();
        } else {
          await deleteCreditCard({ sourceId }).unwrap();
        }

        onRefresh?.();
        showSuccess('Payment method removed');
      } catch (error) {
        console.error('Delete error:', error);
        showError(extractErrorMessage(error, 'Failed to remove payment method'));
        throw error;
      }
    },
    [deleteBankAccount, deleteCreditCard, onRefresh, showSuccess, showError]
  );

  const deleteMethodWithConfirmation = useCallback(
    (method: any) => {
      setMethodToDelete(method);
      setShowDeleteConfirm(true);
    },
    []
  );

  const confirmDelete = useCallback(async () => {
    if (!methodToDelete) return;
    try {
      await deleteMethod(methodToDelete);
    } finally {
      setShowDeleteConfirm(false);
      setMethodToDelete(null);
    }
  }, [methodToDelete, deleteMethod]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setMethodToDelete(null);
  }, []);

  return {
    setAsDefault,
    deleteMethod,
    deleteMethodWithConfirmation,
    showDeleteConfirm,
    confirmDelete,
    cancelDelete,
    isUpdating,
    isDeleting,
  };
};

